import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Edit2,
  Check,
  X,
  Trash2,
  Copy,
  ArrowUp,
  ArrowDown,
  GripVertical,
  ArrowUpDown,
  ArrowUpAZ,
  ArrowDownAZ,
  CalendarClock,
  History,
  ChevronDown,
  Lock,
  Crown,
  Loader2,
  Share2,
  Users,
  LogOut,
  Tag,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNotes } from '../../contexts/NotesContext';
import { resolveFileUrl } from '../../utils/resolveFileUrl';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import DeleteModal from '../Common/DeleteModal';
import ContextMenu from '../Common/ContextMenu';
import { UPGRADE_MESSAGES } from '../../utils/membershipConstants';
import UpgradeModal from '../Upgrade/UpgradeModal';
import AdBanner from '../Advertisement/AdBanner';
import { useMembership } from '../../hooks/useMembership';
import NoteShareModal from './NoteShareModal';
import TransferOwnershipModal from './TransferOwnershipModal';
import CollaboratorsModal from './CollaboratorsModal';
import { useAuth } from '../../contexts/AuthContext';
import { useConnectionMonitor } from '../../contexts/ConnectionMonitorContext';
import { notesApi } from '../../services/cloudApi';
import categoriesApi from '../../services/categoriesApi';
import { getCategoryIcon } from '../../utils/categoryIcons';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

// HTML etiketlerini temizleyip düz metne çeviren fonksiyon
const stripHtmlTags = (html) => {
  if (!html) return '';
  const spaced = html
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ');
  const tmp = document.createElement('DIV');
  tmp.innerHTML = spaced;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

// Notun yalnızca ilk satırını / ilk paragrafını döndürür
const getFirstLinePreview = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  const blocks = tmp.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6');
  for (const block of blocks) {
    const text = (block.textContent || block.innerText || '').trim();
    if (text) return text;
  }
  const fullText = (tmp.textContent || tmp.innerText || '').trim();
  return fullText.split('\n').find((l) => l.trim()) || '';
};

const NotesSidebar = () => {
  const { t, i18n } = useTranslation('notes');
  const dateLocale = getDateFnsLocale(i18n.language);
  const {
    notes,
    selectedNote,
    selectNote,
    createNote,
    updateNote,
    deleteNote,
    duplicateNote,
    moveNoteUp,
    moveNoteDown,
    reorderNotes,
    canCreateMoreNotes,
    remainingNotes,
    maxNoteCount,
    isLoading,
  } = useNotes();
  const { isFreeUser } = useMembership();
  const { authToken } = useAuth();
  const { isServerOnline } = useConnectionMonitor();
  const [showUpgradeToast, setShowUpgradeToast] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [draggedNoteId, setDraggedNoteId] = useState(null);
  const [dragOverNoteId, setDragOverNoteId] = useState(null);
  const [sortMode, setSortMode] = useState('manual');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const [contextMenuState, setContextMenuState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    note: null,
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [noteForTransfer, setNoteForTransfer] = useState(null);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [noteForCollaborators, setNoteForCollaborators] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);

  const fetchApiCategories = useCallback(async () => {
    if (!authToken) return;
    try {
      const data = await categoriesApi.getAll(authToken);
      if (data.ok) setApiCategories(data.categories || []);
    } catch {
      // sessizce devam et
    }
  }, [authToken]);

  useEffect(() => {
    fetchApiCategories();
  }, [fetchApiCategories]);

  // Kategori değişikliği event'ini dinle (ManageCategoriesModal'dan)
  useEffect(() => {
    const handler = () => fetchApiCategories();
    window.addEventListener('notesplus-categories-updated', handler);
    return () => window.removeEventListener('notesplus-categories-updated', handler);
  }, [fetchApiCategories]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSharedOnly, setShowSharedOnly] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForNote, setCategoryForNote] = useState(null);
  const inputRef = useRef(null);
  // Sıralama yalnızca localStorage'da tutulur → çevrimdışı da çalışır
  const canReorder = searchQuery.trim() === '' && sortMode === 'manual';
  // Ortak çalışma notu çevrimdışıyken salt okunur (kişisel notlar düzenlenebilir kalır)
  const isSharedNoteRecord = (note) => !!(note?.isCollaborative || note?.myRole === 'editor');
  const isOfflineLocked = (note) => !isServerOnline && isSharedNoteRecord(note);

  // Toolbar'daki Paylaş butonundan gelen event
  useEffect(() => {
    const handler = (e) => {
      const n = e.detail?.note || selectedNote;
      if (!n) return;
      setNoteToShare(n);
      setShowShareModal(true);
    };
    window.addEventListener('notesplus-open-share-modal', handler);
    return () => window.removeEventListener('notesplus-open-share-modal', handler);
  }, [selectedNote]);

  // Listen for filter events from sidebar
  useEffect(() => {
    const handleCategoryFilter = (event) => {
      const category = event.detail?.category;
      setShowSharedOnly(false);
      setSelectedCategory(category || null);
    };

    const handleSharedFilter = () => {
      setSelectedCategory(null);
      setShowSharedOnly(true);
    };

    const handleClearFilter = () => {
      setSelectedCategory(null);
      setShowSharedOnly(false);
    };

    window.addEventListener('notesplus-filter-category', handleCategoryFilter);
    window.addEventListener('notesplus-filter-shared', handleSharedFilter);
    window.addEventListener('notesplus-filter-clear', handleClearFilter);

    return () => {
      window.removeEventListener('notesplus-filter-category', handleCategoryFilter);
      window.removeEventListener('notesplus-filter-shared', handleSharedFilter);
      window.removeEventListener('notesplus-filter-clear', handleClearFilter);
    };
  }, []);

  const sortOptions = useMemo(
    () => [
      {
        value: 'manual',
        label: t('sidebar.sort.manual.label'),
        description: t('sidebar.sort.manual.description'),
        icon: ArrowUpDown,
      },
      {
        value: 'title-asc',
        label: t('sidebar.sort.titleAsc.label'),
        description: t('sidebar.sort.titleAsc.description'),
        icon: ArrowUpAZ,
      },
      {
        value: 'title-desc',
        label: t('sidebar.sort.titleDesc.label'),
        description: t('sidebar.sort.titleDesc.description'),
        icon: ArrowDownAZ,
      },
      {
        value: 'created-desc',
        label: t('sidebar.sort.createdDesc.label'),
        description: t('sidebar.sort.createdDesc.description'),
        icon: CalendarClock,
      },
      {
        value: 'updated-desc',
        label: t('sidebar.sort.updatedDesc.label'),
        description: t('sidebar.sort.updatedDesc.description'),
        icon: History,
      },
    ],
    [t]
  );

  const sortMeta = useMemo(
    () => sortOptions.find((option) => option.value === sortMode) || sortOptions[0],
    [sortMode, sortOptions]
  );

  // Kategoriler: API'den gelenleri temel al; notlardaki ama listede olmayanları da ekle
  const allCategories = useMemo(() => {
    const catMap = new Map();
    apiCategories.forEach((c) => catMap.set(c.name, c));
    notes.forEach((n) => {
      (n.categories || []).forEach((cat) => {
        if (cat.name && !catMap.has(cat.name)) {
          catMap.set(cat.name, { name: cat.name, color: cat.color || '#3b82f6', icon: null });
        }
      });
    });
    return Array.from(catMap.values()).sort((a, b) => a.name.localeCompare(b.name, i18n.language));
  }, [apiCategories, notes, i18n.language]);

  const filteredNotes = useMemo(() => {
    // Deduplicate by id (keep first occurrence) to avoid React key warnings and duplicate rows (e.g. after sync merge)
    const seen = new Set();
    const uniqueNotes = notes.filter((note) => {
      if (seen.has(note.id)) return false;
      seen.add(note.id);
      return true;
    });

    const normalizedQuery = searchQuery.toLowerCase();
    let base = uniqueNotes.filter((note) => {
      const cleanContent = stripHtmlTags(note.content);
      const matchesSearch =
        note.title.toLowerCase().includes(normalizedQuery) ||
        cleanContent.toLowerCase().includes(normalizedQuery);

      if (showSharedOnly) {
        return matchesSearch && note.isCollaborative === true;
      }

      if (selectedCategory) {
        return matchesSearch && (note.categories || []).some((c) => c.name === selectedCategory);
      }

      return matchesSearch;
    });

    if (sortMode === 'title-asc') {
      return [...base].sort((a, b) =>
        (a.title || '').localeCompare(b.title || '', i18n.language, { sensitivity: 'base' })
      );
    }

    if (sortMode === 'title-desc') {
      return [...base].sort((a, b) =>
        (b.title || '').localeCompare(a.title || '', i18n.language, { sensitivity: 'base' })
      );
    }

    if (sortMode === 'created-desc') {
      return [...base].sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );
    }

    if (sortMode === 'updated-desc') {
      return [...base].sort(
        (a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() -
                  new Date(a.updatedAt || a.createdAt || 0).getTime()
      );
    }

    return base;
  }, [notes, searchQuery, sortMode, selectedCategory, showSharedOnly, i18n.language]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!sortMenuOpen) {
        return;
      }
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortMenuOpen]);

  const handleSelectSort = (value) => {
    setSortMode(value);
    setSortMenuOpen(false);
  };

  const SortIcon = sortMeta.icon || ArrowUpDown;
  const sortButtonTitle = sortMeta.label;

  const finalizeDragState = () => {
    setDraggedNoteId(null);
    setDragOverNoteId(null);
  };

  const handleNoteDragStart = (event, noteId) => {
    if (!canReorder) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    setDraggedNoteId(noteId);
    setDragOverNoteId(noteId);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', noteId);
    }
  };

  const handleNoteDragEnter = (event, noteId) => {
    if (!canReorder || !draggedNoteId || draggedNoteId === noteId) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setDragOverNoteId(noteId);
  };

  const handleNoteDragOver = (event, noteId) => {
    if (!canReorder || !draggedNoteId) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = draggedNoteId === noteId ? 'none' : 'move';
    }
  };

  const handleNoteDragLeave = (noteId) => {
    if (!canReorder || !draggedNoteId) {
      return;
    }
    setDragOverNoteId((prev) => (prev === noteId ? null : prev));
  };

  const handleNoteDrop = (event, targetNoteId) => {
    if (!canReorder || !draggedNoteId) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const sourceId = draggedNoteId;
    finalizeDragState();

    if (sourceId === targetNoteId) {
      return;
    }

    reorderNotes((currentOrder) => {
      const updated = currentOrder.filter((id) => id !== sourceId);
      const targetIndex = updated.indexOf(targetNoteId);

      if (targetIndex === -1) {
        updated.push(sourceId);
        return updated;
      }

      updated.splice(targetIndex, 0, sourceId);
      return updated;
    });
  };

  const handleListDrop = (event) => {
    if (!canReorder || !draggedNoteId || event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const sourceId = draggedNoteId;
    finalizeDragState();

    reorderNotes((currentOrder) => {
      const updated = currentOrder.filter((id) => id !== sourceId);
      updated.push(sourceId);
      return updated;
    });
  };

  const handleNoteDragEnd = (event) => {
    if (event) {
      event.stopPropagation();
    }
    finalizeDragState();
  };

  const handleCreateNote = async () => {
    // Limit kontrolü
    if (!canCreateMoreNotes) {
      setShowUpgradeToast(true);
      // Toast'u 5 saniye sonra kapat
      setTimeout(() => setShowUpgradeToast(false), 5000);
      return;
    }
    
    try {
    const newNote = await createNote();
    if (newNote) {
      selectNote(newNote);
      setEditingNoteId(newNote.id);
      setEditingTitle(newNote.title || '');
      }
    } catch (error) {
      if (error.code === 'NOTE_LIMIT_EXCEEDED') {
        setShowUpgradeToast(true);
        setTimeout(() => setShowUpgradeToast(false), 5000);
      } else {
        console.error('Not oluşturulurken hata:', error);
      }
    }
  };

  const handleEditTitle = (note) => {
    if (isOfflineLocked(note)) {
      notifySharedReadOnly();
      return;
    }
    setEditingNoteId(note.id);
    setEditingTitle(note.title || '');
  };

  const isSavingTitleRef = useRef(false);

  const handleSaveTitle = (note) => {
    if (isOfflineLocked(note)) {
      setEditingNoteId(null);
      setEditingTitle('');
      notifySharedReadOnly();
      return;
    }
    if (isSavingTitleRef.current) return; // çift tetiklenmeyi önle (Enter + blur)
    isSavingTitleRef.current = true;

    const newTitle = editingTitle.trim() || t('common:unnamed');
    const oldTitle = note.title || '';

    setEditingNoteId(null);
    setEditingTitle('');

    // Başlık düzenleme bitti — editördeki Quill'i focusla
    window.dispatchEvent(new CustomEvent('notesplus-focus-editor'));

    if (newTitle !== oldTitle) {
      if (selectedNote?.id === note.id) {
        window.dispatchEvent(new CustomEvent('notesplus-note-title-sync', {
          detail: { noteId: note.id, title: newTitle },
        }));
      }
      updateNote({
        ...note,
        title: newTitle,
        updatedAt: new Date().toISOString(),
      }).catch(() => {
        // API başarısız olursa eski başlığa rollback
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('sidebar.titleUpdateFailed.title'), message: t('sidebar.titleUpdateFailed.message') },
        }));
      });
    }

    setTimeout(() => { isSavingTitleRef.current = false; }, 100);
  };

  const handleCancelEdit = () => {
    isSavingTitleRef.current = true;
    setEditingNoteId(null);
    setEditingTitle('');
    setTimeout(() => { isSavingTitleRef.current = false; }, 100);
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingNoteId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingNoteId]);

  // Handle Enter key to save, Escape to cancel
  const handleKeyDown = (e, note) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle(note);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const notifyOffline = (messageKey) => {
    window.dispatchEvent(
      new CustomEvent('notesplus-show-toast', {
        detail: {
          type: 'warning',
          title: t('editor.offlineToast.title'),
          message: t(messageKey, { ns: 'shared' }),
        },
      })
    );
  };

  // Ortak çalışma notu çevrimdışıyken salt okunur
  const notifySharedReadOnly = () => notifyOffline('readOnlyMode.cannotModify');
  // Silme / paylaşım / ortak çalışma gibi sunucu zorunlu işlemler
  const notifyRequiresConnection = () => notifyOffline('readOnlyMode.requiresConnection');

  const openDeleteConfirmation = (note) => {
    if (!isServerOnline) {
      notifyRequiresConnection();
      return;
    }
    setNoteToDelete(note);
    setDeleteModalOpen(true);
  };

  const handleDeleteClick = (e, note) => {
    e.stopPropagation();
    openDeleteConfirmation(note);
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;
    if (!isServerOnline) {
      notifyRequiresConnection();
      return;
    }
    try {
      const deleted = await deleteNote(noteToDelete.id);
      if (deleted === false) return;
      setDeleteModalOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      if (error?.payload?.error === 'MUST_TRANSFER_OWNERSHIP') {
        setNoteForTransfer(noteToDelete);
        setDeleteModalOpen(false);
        setNoteToDelete(null);
        setShowTransferModal(true);
      } else {
        window.dispatchEvent(
          new CustomEvent('notesplus-show-toast', {
            detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(error, { fallback: t('sidebar.deleteError') }) },
          })
        );
      }
    }
  };

  const handleLeaveNote = async (note) => {
    if (!authToken || !note?.id) return;
    if (!isServerOnline) {
      notifyRequiresConnection();
      return;
    }
    try {
      await notesApi.leaveCollaborativeNote({ token: authToken, noteId: note.id });
      window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
      if (selectedNote?.id === note.id) selectNote(null);
      closeContextMenu();
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'success', title: t('sidebar.leaveNote.successTitle'), message: t('sidebar.leaveNote.successMessage') },
        })
      );
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('sidebar.leaveNote.errorMessage') }) },
        })
      );
    }
  };

  const handleContextMenu = (event, note) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuState({
      isOpen: true,
      position: { x: event.clientX, y: event.clientY },
      note,
    });
  };

  const closeContextMenu = () => {
    setContextMenuState((prev) => ({ ...prev, isOpen: false, note: null }));
  };

  const handleDuplicateNote = async (note) => {
    const duplicated = await duplicateNote(note.id);
    if (duplicated) {
      selectNote(duplicated);
    }
  };

  const handleShareNote = (note) => {
    if (!isServerOnline) {
      notifyRequiresConnection();
      closeContextMenu();
      return;
    }
    setNoteToShare(note);
    setShowShareModal(true);
    closeContextMenu();
  };

  const contextMenuItems = contextMenuState.note
    ? [
        {
          label: t('sidebar.contextMenu.addCategory'),
          icon: Tag,
          disabled: isOfflineLocked(contextMenuState.note),
          onClick: () => {
            if (isOfflineLocked(contextMenuState.note)) {
              notifySharedReadOnly();
              closeContextMenu();
              return;
            }
            const targetNote = contextMenuState.note;
            if (!targetNote) return;
            setCategoryForNote(targetNote);
            setShowCategoryModal(true);
            closeContextMenu();
          },
        },
        {
          label: t('common:edit'),
          icon: Edit2,
          disabled: isOfflineLocked(contextMenuState.note),
          onClick: () => {
            const targetNote = contextMenuState.note;
            if (!targetNote) return;
            selectNote(targetNote);
            handleEditTitle(targetNote);
          },
        },
        {
          label: t('sidebar.contextMenu.duplicate'),
          icon: Copy,
          onClick: () => {
            const targetNote = contextMenuState.note;
            if (!targetNote) return;
            handleDuplicateNote(targetNote);
          },
        },
        {
          label: t('sidebar.contextMenu.share'),
          icon: Share2,
          disabled: !isServerOnline,
          onClick: () => {
            const targetNote = contextMenuState.note;
            if (!targetNote) return;
            handleShareNote(targetNote);
          },
        },
        ...(contextMenuState.note?.isCollaborative
          ? [
              {
                label: contextMenuState.note?.myRole === 'owner' ? t('sidebar.contextMenu.manageCollaborators') : t('sidebar.contextMenu.leaveNote'),
                icon: contextMenuState.note?.myRole === 'owner' ? Users : LogOut,
                disabled: !isServerOnline,
                onClick: () => {
                  if (!isServerOnline) {
                    notifyRequiresConnection();
                    closeContextMenu();
                    return;
                  }
                  const targetNote = contextMenuState.note;
                  if (!targetNote) return;
                  if (contextMenuState.note?.myRole === 'owner') {
                    setNoteForCollaborators(targetNote);
                    setShowCollaboratorsModal(true);
                    closeContextMenu();
                  } else {
                    handleLeaveNote(targetNote);
                  }
                },
              },
            ]
          : []),
        {
          label: t('common:delete'),
          icon: Trash2,
          disabled: !isServerOnline,
          onClick: () => {
            const targetNote = contextMenuState.note;
            if (!targetNote) return;
            openDeleteConfirmation(targetNote);
          },
          danger: true,
        },
      ]
    : [];

  return (
    <div className="relative flex flex-col h-full overflow-hidden bg-transparent">
      {/* Upgrade Toast */}
      <AnimatePresence>
        {showUpgradeToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-2 left-2 right-2 z-50 p-4 bg-[#1a2640] border border-white/10 rounded-md shadow-elevated"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[var(--color-surface-hover)] rounded-sm flex items-center justify-center">
                <Crown className="w-5 h-5 text-[#e4edf7]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#e4edf7] text-sm">{t('sidebar.upgradeToast.title')}</p>
                <p className="text-[#c8d8eb] text-xs mt-0.5">
                  {UPGRADE_MESSAGES.noteLimit}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="px-3 py-1.5 bg-[var(--color-surface-hover)] hover:opacity-90 rounded-sm text-[#e4edf7] text-xs font-medium transition-colors"
                >
                  {t('sidebar.upgradeToast.details')}
                </button>
                <button
                  onClick={() => setShowUpgradeToast(false)}
                  className="flex-shrink-0 p-1 hover:bg-[var(--color-surface-hover)] rounded-sm transition-colors"
                >
                  <X className="w-4 h-4 text-[#e4edf7]" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Header */}
      <div className="h-16 flex items-center px-3 border-b border-white/[0.05] shrink-0 gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#4a6280] pointer-events-none" />
          <input
            type="text"
            placeholder={t('common:search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-[#161f31] border border-white/[0.07] rounded-lg text-xs text-[#c8d8eb] placeholder-[#4a6280] focus:outline-none focus:border-[#3d8ef0]/50 focus:bg-[#1a2640] transition-colors"
          />
        </div>
        {/* Sort button */}
        <div className="relative" ref={sortMenuRef}>
          <button
            onClick={() => setSortMenuOpen((o) => !o)}
            title={sortButtonTitle}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#161f31] border border-white/[0.07] text-[#7a96b0] hover:text-[#c8d8eb] hover:border-white/[0.12] transition-colors flex-shrink-0"
          >
            <SortIcon className="w-3.5 h-3.5" />
          </button>
          {sortMenuOpen && (
            <div className="absolute right-0 top-full mt-1.5 z-50 bg-[#0d1424] border border-white/[0.09] rounded-xl shadow-[0_12px_40px_rgba(0,0,0,0.6)] py-1 min-w-[220px]">
              {sortOptions.map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelectSort(opt.value)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors ${
                      sortMode === opt.value
                        ? 'text-[#3d8ef0] bg-[#2563eb]/10'
                        : 'text-[#c8d8eb] hover:bg-white/[0.05]'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Notes List */}
      <div
        className="flex-1 overflow-y-auto relative px-2 py-2"
        onDragOver={(event) => {
          if (!canReorder || !draggedNoteId) return;
          event.preventDefault();
          event.stopPropagation();
        }}
        onDrop={handleListDrop}
      >
        <AnimatePresence>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-5 h-5 text-[#3d8ef0] animate-spin" />
              <p className="text-xs text-[#4a6280]">{t('loading')}</p>
            </div>
          ) : filteredNotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#161f31] border border-white/[0.06] flex items-center justify-center">
                <Search className="w-4 h-4 text-[#4a6280]" />
              </div>
              <p className="text-xs text-[#4a6280]">
                {searchQuery ? t('sidebar.empty.noResults') : showSharedOnly ? t('sidebar.empty.noSharedNotes') : t('sidebar.empty.noNotesYet')}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredNotes.map((note) => {
                const isDragged = draggedNoteId === note.id;
                const isDragOver = dragOverNoteId === note.id && draggedNoteId !== note.id;
                const isSelected = selectedNote?.id === note.id;
                const preview = getFirstLinePreview(note.content);

                return (
                  <motion.div
                    key={note.id}
                    draggable={canReorder && editingNoteId !== note.id}
                    onDragStart={(event) => handleNoteDragStart(event, note.id)}
                    onDragEnter={(event) => handleNoteDragEnter(event, note.id)}
                    onDragOver={(event) => handleNoteDragOver(event, note.id)}
                    onDragLeave={() => handleNoteDragLeave(note.id)}
                    onDrop={(event) => handleNoteDrop(event, note.id)}
                    onDragEnd={handleNoteDragEnd}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className={`group relative rounded-xl cursor-pointer transition-all duration-150 ${
                      isSelected
                        ? 'bg-[#1a2640] border border-[#3d8ef0]/30 shadow-[0_0_0_1px_rgba(61,142,240,0.1)]'
                        : isDragOver
                        ? 'bg-[#1a2640] border border-[#3d8ef0]/20'
                        : 'bg-[#111827] border border-white/[0.05] hover:bg-[#161f31] hover:border-white/[0.09]'
                    } ${isDragged ? 'opacity-60 scale-[0.98]' : ''}`}
                    onClick={(e) => {
                      if (editingNoteId === note.id || e.target.closest('button') || e.target.closest('input')) return;
                      selectNote(note);
                    }}
                    onContextMenu={(event) => handleContextMenu(event, note)}
                  >
                    {/* Seçili left accent bar */}
                    {isSelected && (
                      <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-[#2563eb]" />
                    )}

                    <div className="px-3.5 py-3">
                      {/* Başlık satırı */}
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        {editingNoteId === note.id ? (
                          <input
                            ref={inputRef}
                            type="text"
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            onBlur={() => handleSaveTitle(note)}
                            onKeyDown={(e) => handleKeyDown(e, note)}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-semibold flex-1 bg-transparent border-b border-[#3d8ef0]/60 focus:outline-none text-[#e4edf7] pb-0.5"
                          />
                        ) : (
                          <h3 className={`text-sm font-semibold leading-snug truncate flex-1 ${
                            isSelected ? 'text-white' : 'text-[#dde8f5]'
                          }`}>
                            {note.title || t('common:unnamed')}
                          </h3>
                        )}
                        <span className={`text-[10px] whitespace-nowrap shrink-0 mt-0.5 ${
                          isSelected ? 'text-[#7ab8f8]' : 'text-[#3d5470]'
                        }`}>
                          {format(new Date(note.updatedAt || note.createdAt), 'dd MMM', { locale: dateLocale })}
                        </span>
                      </div>

                      {/* İçerik önizleme */}
                      {preview && (
                        <p className={`text-[11px] leading-relaxed truncate mb-2 ${
                          isSelected ? 'text-[#8aaccc]' : 'text-[#3d5470]'
                        }`}>
                          {preview}
                        </p>
                      )}

                      {/* Kategoriler + collab avatarları */}
                      {((note.categories?.length > 0) || note.isCollaborative) && (
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {(note.categories || []).map((cat) => {
                            const catColor = cat.color || '#3b82f6';
                            const apiCat = allCategories.find((c) => c.name === cat.name);
                            const CatIcon = getCategoryIcon(apiCat?.icon);
                            return (
                              <span
                                key={cat.name}
                                className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-md text-[10px] font-medium group/cat"
                                style={{ backgroundColor: `${catColor}18`, color: catColor, border: `1px solid ${catColor}28` }}
                              >
                                <CatIcon className="w-2.5 h-2.5 flex-shrink-0" />
                                <span>{cat.name}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateNote({
                                      ...note,
                                      categories: (note.categories || []).filter((c) => c.name !== cat.name),
                                      updatedAt: new Date().toISOString(),
                                    }).catch(() => { /* çevrimdışı ortak not: NotesContext uyarıyor */ });
                                  }}
                                  className="opacity-0 group-hover/cat:opacity-100 transition-opacity ml-0.5 leading-none"
                                  title={t('sidebar.removeCategoryTooltip')}
                                >
                                  <X className="w-2.5 h-2.5" />
                                </button>
                              </span>
                            );
                          })}
                          {note.isCollaborative && note.collaborators?.length > 0 && (
                            <CollaboratorAvatars
                              collaborators={note.collaborators}
                              onClick={(e) => {
                                e.stopPropagation();
                                setNoteForCollaborators(note);
                                setShowCollaboratorsModal(true);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating Action Button */}
      <motion.button
        whileHover={canCreateMoreNotes ? { scale: 1.05 } : {}}
        whileTap={canCreateMoreNotes ? { scale: 0.95 } : {}}
        onClick={handleCreateNote}
        disabled={!canCreateMoreNotes}
        className={`absolute bottom-5 right-4 w-11 h-11 rounded-2xl flex items-center justify-center text-white z-50 shadow-lg transition-all ${
          canCreateMoreNotes
            ? 'bg-[#2563eb] hover:bg-[#3d8ef0] shadow-[#3d8ef0]/30 cursor-pointer'
            : 'bg-[#161f31] cursor-not-allowed'
        }`}
        title={
          canCreateMoreNotes
            ? t('sidebar.fab.createTitle')
            : t('sidebar.fab.limitReachedTitle')
        }
      >
        {canCreateMoreNotes ? (
          <Plus className="w-5 h-5" strokeWidth={2.5} />
        ) : (
          <Lock className="w-4 h-4 text-[#7a96b0]" strokeWidth={2} />
        )}
      </motion.button>


      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setNoteToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('sidebar.deleteModal.title')}
        message={noteToDelete ? t('sidebar.deleteModal.confirmWithTitle', { title: noteToDelete.title || t('common:unnamed') }) : t('sidebar.deleteModal.confirmGeneric')}
      />
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        trigger="note_limit"
      />
      <ContextMenu
        isOpen={contextMenuState.isOpen}
        position={contextMenuState.position}
        items={contextMenuItems}
        onClose={closeContextMenu}
      />
      <NoteShareModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setNoteToShare(null);
        }}
        note={noteToShare}
      />
      <TransferOwnershipModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setNoteForTransfer(null);
        }}
        note={noteForTransfer}
        onTransferred={() => {
          setShowTransferModal(false);
          setNoteForTransfer(null);
        }}
      />
      <CollaboratorsModal
        isOpen={showCollaboratorsModal}
        onClose={() => {
          setShowCollaboratorsModal(false);
          setNoteForCollaborators(null);
        }}
        note={noteForCollaborators}
      />

      {/* Category Assignment Modal */}
      <AnimatePresence>
        {showCategoryModal && categoryForNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => { setShowCategoryModal(false); setCategoryForNote(null); }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 8 }}
              transition={{ duration: 0.16 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.7)] w-full max-w-sm flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.07]">
                <div className="w-8 h-8 rounded-xl bg-[#2563eb]/15 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-[#3d8ef0]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[#e4edf7] font-bold text-sm">{t('sidebar.categoryModal.title')}</h3>
                  <p className="text-[#7a96b0] text-xs truncate mt-0.5">{categoryForNote.title || t('sidebar.categoryModal.fallbackNoteName')}</p>
                </div>
                <button
                  onClick={() => { setShowCategoryModal(false); setCategoryForNote(null); }}
                  className="p-1.5 text-[#7a96b0] hover:text-[#c8d8eb] hover:bg-white/[0.05] rounded-xl transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Category list */}
              <div className="p-3 max-h-72 overflow-y-auto">
                {allCategories.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <Tag className="w-8 h-8 text-[#3d8ef1]" />
                    <p className="text-xs text-[#7a96b0] text-center">
                      {t('sidebar.categoryModal.emptyState')}<br />
                      <span className="text-[#3d8ef0]">{t('sidebar.categoryModal.manageCategoriesLink')}</span>{t('sidebar.categoryModal.manageCategoriesSuffix')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {allCategories.map((cat) => {
                      const catName = cat.name;
                      const catColor = cat.color || '#3b82f6';
                      const CatIcon = getCategoryIcon(cat.icon);
                      // Canlı notdan kategorileri oku (modal açıkken değişebilir)
                      const liveNote = notes.find((n) => n.id === categoryForNote?.id) || categoryForNote;
                      const currentCats = liveNote?.categories || [];
                      const isSelected = currentCats.some((c) => c.name === catName);
                      return (
                        <button
                          key={catName}
                          onClick={() => {
                            const newCats = isSelected
                              ? currentCats.filter((c) => c.name !== catName)
                              : [...currentCats, { name: catName, color: catColor }];
                            updateNote({
                              ...liveNote,
                              categories: newCats,
                              updatedAt: new Date().toISOString(),
                            }).catch(() => { /* çevrimdışı ortak not: NotesContext uyarıyor */ });
                            // Modal açık kalır — çoklu seçim için
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                            isSelected
                              ? 'bg-[#161f31] border border-white/10'
                              : 'hover:bg-[#161f31] border border-transparent'
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: catColor + '22' }}
                          >
                            <CatIcon className="w-4 h-4" style={{ color: catColor }} />
                          </div>
                          <span className="flex-1 text-sm font-medium text-[#c8d8eb] truncate">{catName}</span>
                          {isSelected && (
                            <Check className="w-4 h-4 flex-shrink-0" style={{ color: catColor }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {(() => {
                const liveNote = notes.find((n) => n.id === categoryForNote?.id) || categoryForNote;
                const currentCats = liveNote?.categories || [];
                return currentCats.length > 0 ? (
                  <div className="px-3 pb-3">
                    <button
                      onClick={() => {
                        updateNote({ ...(liveNote || categoryForNote), categories: [], updatedAt: new Date().toISOString() })
                          .catch(() => { /* çevrimdışı ortak not: NotesContext uyarıyor */ });
                        setShowCategoryModal(false);
                        setCategoryForNote(null);
                      }}
                      className="w-full py-2 text-xs font-medium text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
                    >
                      {t('sidebar.categoryModal.removeAll')}
                    </button>
                  </div>
                ) : null;
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const getInitials = (name, email) => {
  if (name?.trim()) {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.trim().slice(0, 2).toUpperCase();
  }
  return email?.trim().slice(0, 2).toUpperCase() || '?';
};

const CollabAvatar = ({ collaborator }) => {
  const { t } = useTranslation('notes');
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!collaborator.avatar_url) return;
    if (collaborator.avatar_url.startsWith('http')) {
      setSrc(collaborator.avatar_url);
      return;
    }
    resolveFileUrl(collaborator.avatar_url).then(setSrc).catch(() => setSrc(null));
  }, [collaborator.avatar_url]);

  const initials = getInitials(collaborator.name, collaborator.email);
  const label = collaborator.name || collaborator.email || '';

  if (src) {
    return (
      <img
        src={src}
        alt={label}
        title={`${label}${collaborator.role === 'owner' ? t('sidebar.ownerSuffix') : ''}`}
        className="w-5 h-5 rounded-full object-cover border-2 border-white dark:border-[#161f31] -ml-1.5 first:ml-0"
      />
    );
  }

  return (
    <div
      title={`${label}${collaborator.role === 'owner' ? t('sidebar.ownerSuffix') : ''}`}
      className="w-5 h-5 rounded-full border-2 border-white dark:border-[#161f31] -ml-1.5 first:ml-0 bg-[#2563eb]/10 dark:bg-[#2563eb]/10/40 flex items-center justify-center"
    >
      <span className="text-[8px] font-bold text-[#3d8ef0] dark:text-primary-300 leading-none">
        {initials}
      </span>
    </div>
  );
};

const CollaboratorAvatars = ({ collaborators, onClick }) => {
  const { t } = useTranslation('notes');
  if (!collaborators?.length) return null;
  const visible = collaborators.slice(0, 4);
  const extra = collaborators.length - visible.length;

  return (
    <button
      onClick={onClick}
      className="cursor-pointer hover:opacity-80 transition-opacity flex items-center mt-1.5"
      title={t('sidebar.collaboratorsTooltip')}
    >
      <div className="flex items-center">
        {visible.map((c) => (
          <CollabAvatar key={c.user_id} collaborator={c} />
        ))}
        {extra > 0 && (
          <div className="w-5 h-5 rounded-full border-2 border-white dark:border-[#161f31] -ml-1.5 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
            <span className="text-[8px] font-bold text-slate-600 dark:text-slate-300 leading-none">
              +{extra}
            </span>
          </div>
        )}
      </div>
      <span className="ml-2 text-[10px] text-slate-400 dark:text-slate-500">
        {t('sidebar.peopleCount', { count: collaborators.length })}
      </span>
    </button>
  );
};

export default NotesSidebar;

